import { gcpIamMcpTools } from "./tools.ts";

/**
 * Maps `gcpStorageMcp`'s tool names to the GCP IAM permission(s) each one
 * actually exercises, for risk resolution against the IAM Risk Catalog
 * (`@p0security/catalog`).
 *
 * `gcpStorageMcp` bridges transparently to Google's own hosted MCP server
 * (`storage.googleapis.com/storage/mcp`) rather than a P0-authored tool
 * list, so — unlike other predefined integrations — there's no local tool
 * definition to attach this mapping to. It's maintained here as a
 * standalone table instead, hand-verified against Google's live tool list
 * and the IAM Risk Catalog's `services/gcp/storage/*.yml` source. Nothing
 * here notices if Google adds, renames, or removes a tool — the resolver
 * that reads this table logs an ops warning for any Storage tool name it
 * doesn't recognize, so drift is observable rather than silent (see
 * ENG-8871).
 */
export const gcpStorageMcpToolIamPermissions: Record<string, string[]> = {
  list_buckets: ["storage.buckets.list"],
  list_objects: ["storage.objects.list"],
  create_bucket: ["storage.buckets.create"],
  delete_bucket: ["storage.buckets.delete"],
  // GCS has no separate "read metadata" permission — storage.objects.get
  // gates both content and metadata reads, so this carries the same risk
  // as read_object even though it never returns object content.
  get_object_metadata: ["storage.objects.get"],
  read_object: ["storage.objects.get"],
  // Deprecated alias of read_object (same underlying GCS operation).
  read_text: ["storage.objects.get"],
  delete_object: ["storage.objects.delete"],
  // Overwriting an existing object is still a create in GCS's IAM model
  // (uploads replace the live generation atomically) — storage.objects.update
  // only applies to object metadata, not content.
  write_text: ["storage.objects.create"],
};

/** Tool name -> IAM permission(s), derived from {@link gcpIamMcpTools}'s own
 * `iamPermissions` field. */
export const gcpIamMcpToolIamPermissions: Record<string, string[]> =
  Object.fromEntries(
    gcpIamMcpTools.flatMap((tool) =>
      tool.iamPermissions ? [[tool.name, tool.iamPermissions] as const] : []
    )
  );

/**
 * Maps `gcpBigQueryMcp`'s tool names to the GCP IAM permission(s) each one
 * actually exercises. Same standalone-table rationale as
 * {@link gcpStorageMcpToolIamPermissions} (web-hydrated from Google's own
 * hosted MCP server, no local tool list to attach this to), hand-verified
 * against a live `tools/list` call to `bigquery.googleapis.com/mcp` and the
 * IAM Risk Catalog's `services/gcp/bigquery/*.yml` source.
 */
export const gcpBigQueryMcpToolIamPermissions: Record<string, string[]> = {
  list_dataset_ids: ["bigquery.datasets.get"],
  get_dataset_info: ["bigquery.datasets.get"],
  list_table_ids: ["bigquery.tables.list"],
  get_table_info: ["bigquery.tables.get"],
  // bigquery.jobs.create gates every job type (query, load, export, copy),
  // including arbitrary DML/DDL — it's the sole permission the catalog
  // scores unconditionally at the ceiling (CRITICAL), same reasoning as
  // gcpIamMcpTools' set_service_account_iam_policy. Both execute_sql tools
  // map to it regardless of the SQL text they're passed.
  execute_sql_readonly: ["bigquery.jobs.create"],
  execute_sql: ["bigquery.jobs.create"],
  get_query_results: ["bigquery.jobs.get"],
  cancel_job: ["bigquery.jobs.update"],
  get_job: ["bigquery.jobs.get"],
};

/**
 * Maps `gcpComputeMcp`'s tool names to the GCP IAM permission(s) each one
 * actually exercises. Same standalone-table rationale as
 * {@link gcpStorageMcpToolIamPermissions}, hand-verified against a live
 * `tools/list` call to `compute.googleapis.com/mcp` and the IAM Risk
 * Catalog's `services/gcp/compute/*.yml` source.
 */
export const gcpComputeMcpToolIamPermissions: Record<string, string[]> = {
  create_instance: ["compute.instances.create"],
  delete_instance: ["compute.instances.delete"],
  start_instance: ["compute.instances.start"],
  stop_instance: ["compute.instances.stop"],
  reset_instance: ["compute.instances.reset"],
  get_instance_basic_info: ["compute.instances.get"],
  set_instance_machine_type: ["compute.instances.setMachineType"],
  // Attached-disk info is returned as part of an instance's own get/list
  // representation — Compute has no separate "list attached disks" method
  // or permission.
  list_instance_attached_disks: ["compute.instances.get"],
  list_instances: ["compute.instances.list"],
  get_instance_group_manager_basic_info: ["compute.instanceGroupManagers.get"],
  list_instance_group_managers: ["compute.instanceGroupManagers.list"],
  // Google's own REST reference confirms this method is gated by the same
  // permission as reading the MIG itself — no distinct "list managed
  // instances" permission exists.
  list_managed_instances: ["compute.instanceGroupManagers.get"],
  list_instance_templates: ["compute.instanceTemplates.list"],
  get_instance_template_basic_info: ["compute.instanceTemplates.get"],
  // Properties is a different field subset of the same get response — no
  // separate permission gates it, per compute.instanceTemplates.get.
  get_instance_template_properties: ["compute.instanceTemplates.get"],
  get_disk_basic_info: ["compute.disks.get"],
  // Performance config is part of a disk's own metadata — no separate
  // permission gates it.
  get_disk_performance_config: ["compute.disks.get"],
  list_disks: ["compute.disks.list"],
  list_accelerator_types: ["compute.acceleratorTypes.list"],
  list_images: ["compute.images.list"],
  list_commitments: ["compute.commitments.list"],
  get_commitment_basic_info: ["compute.commitments.get"],
  get_reservation_basic_info: ["compute.reservations.get"],
  get_reservation_details: ["compute.reservations.get"],
  list_reservations: ["compute.reservations.list"],
  // Lists reservations filtered to a single commitment, server-side — same
  // underlying permission as listing reservations project-wide.
  list_commitment_reservations: ["compute.reservations.list"],
  list_machine_types: ["compute.machineTypes.list"],
  get_zone_operation: ["compute.zoneOperations.get"],
  list_snapshots: ["compute.snapshots.list"],
};
